import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { FadeUp, SectionTag } from "../components/Motion";
import { FOUNDERS, IMAGES, TIMELINE } from "../data/content";

const STATS = [
  { n: "12", l: "Years in the fight" },
  { n: "100+", l: "Brand engagements" },
  { n: "07", l: "Service lines, one roof" },
  { n: "01", l: "Obsession — attention" },
];

const About = () => (
  <>
    <PageHeader
      tag="About — The second act"
      title={
        <>
          Twelve years of Myndroot.<br />
          <span className="text-nexa-red">Now it has teeth.</span>
        </>
      }
      sub="Nexa Studio is not a fresh start. It is a loaded one — a new brand carrying every scar, case file and trophy from 12 years of building Myndroot."
    />

    <section className="mx-auto max-w-[1600px] px-5 py-20 md:px-10" data-testid="stats-strip">
      <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        {STATS.map((s) => (
          <div key={s.l} className="bg-nexa-black p-8">
            <p className="font-display text-6xl text-nexa-red">{s.n}</p>
            <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-nexa-bone/50">{s.l}</p>
          </div>
        ))}
      </div>
    </section>

    {/* Founders */}
    <section className="border-t border-white/10 bg-nexa-surface/40" data-testid="founder-section">
      <div className="mx-auto max-w-[1600px] px-5 py-24 md:px-10">
        <SectionTag>The founders</SectionTag>
        <FadeUp>
          <h2 className="mt-4 font-display text-4xl uppercase leading-[0.95] text-nexa-bone md:text-6xl">
            Two people. Twelve years.<br />
            <span className="text-nexa-red">One temper.</span>
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-nexa-bone/60">
            For twelve years, Indira Mukherjee and Ankit Bhattacharyya built Myndroot from a small Kolkata room into
            Eastern India's battle tested brand and digital agency — work for FIFA U-17, the IPL, TATA Steel, OPPO,
            CESC and a hundred brands in between. Nexa Studio is their second act. Not a rebrand, not a rename — a
            new animal, bred from everything Myndroot learned the hard way: that attention is earned daily, that
            safe is expensive, and that a brand without teeth is just decoration.
          </p>
        </FadeUp>
        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {FOUNDERS.map((f, i) => (
            <FadeUp key={f.name} delay={i * 0.1}>
              <div
                className="group flex h-full flex-col border border-white/10 bg-nexa-black transition-colors duration-300 hover:border-nexa-red"
                data-testid={`founder-card-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="overflow-hidden">
                  <img
                    src={f.img}
                    alt={f.name}
                    loading="lazy"
                    className={`h-96 w-full object-cover ${f.pos} transition-transform duration-500 group-hover:scale-[1.03]`}
                  />
                </div>
                <div className="p-8 md:p-10">
                  <h3 className="font-display text-3xl uppercase text-nexa-bone">{f.name}</h3>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-nexa-red">{f.role}</p>
                  <p className="mt-4 text-sm leading-relaxed text-nexa-bone/60">{f.bio}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    {/* Timeline */}
    <section className="mx-auto max-w-[1600px] px-5 py-24 md:px-10" data-testid="timeline-section">
      <SectionTag>The road here</SectionTag>
      <div className="mt-10 border-t border-white/10">
        {TIMELINE.map((t, i) => (
          <FadeUp key={t.year} delay={i * 0.04}>
            <div className="grid items-baseline gap-2 border-b border-white/10 py-6 md:grid-cols-12">
              <span className={`font-display text-4xl md:col-span-2 ${t.year === "2026" ? "text-nexa-red" : "text-nexa-bone/80"}`}>
                {t.year}
              </span>
              <p className="text-sm leading-relaxed text-nexa-bone/60 md:col-span-9 md:text-base">{t.event}</p>
              {t.year === "2026" && (
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-nexa-red md:col-span-1">You are here</span>
              )}
            </div>
          </FadeUp>
        ))}
      </div>
    </section>

    {/* Culture */}
    <section className="border-t border-white/10" data-testid="culture-section">
      <div className="mx-auto grid max-w-[1600px] items-center gap-12 px-5 py-24 md:grid-cols-2 md:px-10">
        <FadeUp>
          <SectionTag>Inside the den</SectionTag>
          <h2 className="mt-4 font-display text-4xl uppercase leading-none text-nexa-bone md:text-5xl">
            Young guns.<br />Greybeards.<br />
            <span className="text-nexa-red">One drumbeat.</span>
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-nexa-bone/60">
            Strategists who sketch, designers who read dashboards, editors who argue about kerning at midnight.
            The studio runs like a newsroom — publishing, reacting, provoking, every day.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-contact"
            className="group mt-8 inline-flex items-center gap-3 bg-nexa-red px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-transform duration-200 hover:-translate-y-1 hover:bg-nexa-bone hover:text-nexa-black"
          >
            Meet us <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
        <FadeUp delay={0.15}>
          <div className="overflow-hidden border border-white/10">
            <img src={IMAGES.team} alt="Nexa Studio team at work" className="h-[380px] w-full object-cover grayscale-[35%]" loading="lazy" />
          </div>
        </FadeUp>
      </div>
    </section>
  </>
);

export default About;
